import { openDB, DBSchema, IDBPDatabase } from 'idb';

// --- Database Schema ---

interface NudgeOfflineDB extends DBSchema {
  apiCache: {
    key: string;
    value: { data: unknown; cachedAt: number };
  };
  offlineQueue: {
    key: number;
    value: QueuedMutation;
    indexes: { 'by-createdAt': number };
  };
  tempIdMap: {
    key: string;
    value: number;
  };
}

export interface QueuedMutation {
  id?: number;
  type:
    | 'addContact'
    | 'updateContact'
    | 'deleteContact'
    | 'addInteraction'
    | 'deleteInteraction'
    | 'updateProfile';
  payload: unknown;
  tempId?: string;
  dependsOnTempId?: string;
  createdAt: number;
  retries: number;
}

// --- Lazy Singleton ---

let dbPromise: Promise<IDBPDatabase<NudgeOfflineDB>> | null = null;

function getDB(): Promise<IDBPDatabase<NudgeOfflineDB>> {
  if (!dbPromise) {
    dbPromise = openDB<NudgeOfflineDB>('nudge-offline', 1, {
      upgrade(db) {
        db.createObjectStore('apiCache');

        const queueStore = db.createObjectStore('offlineQueue', {
          keyPath: 'id',
          autoIncrement: true,
        });
        queueStore.createIndex('by-createdAt', 'createdAt');

        db.createObjectStore('tempIdMap');
      },
    });
  }
  return dbPromise;
}

// --- API Cache ---

export async function getCached<T>(
  key: string,
): Promise<{ data: T; cachedAt: number } | undefined> {
  try {
    const db = await getDB();
    const entry = await db.get('apiCache', key);
    return entry as { data: T; cachedAt: number } | undefined;
  } catch {
    return undefined;
  }
}

export async function setCached<T>(key: string, data: T): Promise<void> {
  try {
    const db = await getDB();
    await db.put('apiCache', { data, cachedAt: Date.now() }, key);
  } catch {
    // IDB write failed — non-critical, ignore
  }
}

export async function deleteCached(key: string): Promise<void> {
  try {
    const db = await getDB();
    await db.delete('apiCache', key);
  } catch {
    // ignore
  }
}

export async function clearCache(): Promise<void> {
  try {
    const db = await getDB();
    await db.clear('apiCache');
  } catch {
    // ignore
  }
}

export async function clearCacheByPrefix(prefix: string): Promise<void> {
  try {
    const db = await getDB();
    const tx = db.transaction('apiCache', 'readwrite');
    const store = tx.objectStore('apiCache');
    let cursor = await store.openCursor();
    while (cursor) {
      if ((cursor.key as string).startsWith(prefix)) {
        await cursor.delete();
      }
      cursor = await cursor.continue();
    }
    await tx.done;
  } catch {
    // ignore
  }
}

// --- Offline Queue ---

const MAX_QUEUE_SIZE = 100;

export async function enqueue(
  mutation: Omit<QueuedMutation, 'id' | 'createdAt' | 'retries'>,
): Promise<boolean> {
  try {
    const db = await getDB();
    const count = await db.count('offlineQueue');
    if (count >= MAX_QUEUE_SIZE) return false;
    await db.add('offlineQueue', {
      ...mutation,
      createdAt: Date.now(),
      retries: 0,
    } as QueuedMutation);
    return true;
  } catch {
    return false;
  }
}

export async function dequeueAll(): Promise<QueuedMutation[]> {
  try {
    const db = await getDB();
    return await db.getAllFromIndex('offlineQueue', 'by-createdAt');
  } catch {
    return [];
  }
}

export async function removeMutation(id: number): Promise<void> {
  try {
    const db = await getDB();
    await db.delete('offlineQueue', id);
  } catch {
    // ignore
  }
}

export async function getQueueLength(): Promise<number> {
  try {
    const db = await getDB();
    return await db.count('offlineQueue');
  } catch {
    return 0;
  }
}

export async function clearQueue(): Promise<void> {
  try {
    const db = await getDB();
    await db.clear('offlineQueue');
  } catch {
    // ignore
  }
}

// --- Temp ID Map (for offline contact creates → server ID resolution) ---

export async function setTempIdMapping(tempId: string, serverId: number): Promise<void> {
  try {
    const db = await getDB();
    await db.put('tempIdMap', serverId, tempId);
  } catch {
    // ignore
  }
}

export async function getTempIdMap(): Promise<Record<string, number>> {
  try {
    const db = await getDB();
    const tx = db.transaction('tempIdMap', 'readonly');
    const store = tx.objectStore('tempIdMap');
    const map: Record<string, number> = {};
    let cursor = await store.openCursor();
    while (cursor) {
      map[cursor.key as string] = cursor.value;
      cursor = await cursor.continue();
    }
    return map;
  } catch {
    return {};
  }
}

export async function clearTempIdMap(): Promise<void> {
  try {
    const db = await getDB();
    await db.clear('tempIdMap');
  } catch {
    // ignore
  }
}
